import React, { useEffect, useState } from 'react';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { IconButton, Tooltip } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FolderZipOutlinedIcon from '@mui/icons-material/FolderZipOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { CopyButton, HighlightedCode } from './CodeBlock';
import { normaliseLanguage, languageLabel } from './code/highlighter';
import '../Assets/css/ChatUI.css';

// Side panel that shows a generated multi-file project like an IDE
const CodeEditorPane = ({ files, title = 'Terraform project', onClose }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  // New answer -> start on its first file
  useEffect(() => setActiveIndex(0), [files]);

  if (!files || files.length === 0) return null;
  const active = files[Math.min(activeIndex, files.length - 1)];
  const language = normaliseLanguage(null, active.name);
  const lines = active.content.replace(/\n$/, '').split('\n').length;

  const downloadFile = () => {
    saveAs(new Blob([active.content], { type: 'text/plain;charset=utf-8' }), active.name);
  };

  const downloadAll = async () => {
    const zip = new JSZip();
    files.forEach((file) => zip.file(file.name, file.content));
    saveAs(await zip.generateAsync({ type: 'blob' }), 'terraform.zip');
  };

  return (
    <aside className="editor-pane" aria-label="Code editor">
      <header className="editor-titlebar">
        <div className="editor-title">
          <span className="editor-dots" aria-hidden="true"><i /><i /><i /></span>
          <strong>{title}</strong>
          <span className="editor-subtitle">{files.length} file{files.length === 1 ? '' : 's'}</span>
        </div>
        <div className="editor-title-actions">
          <Tooltip title="Download all as .zip" arrow>
            <button type="button" className="editor-button" onClick={downloadAll}>
              <FolderZipOutlinedIcon sx={{ fontSize: 16 }} />
              <span>Download all</span>
            </button>
          </Tooltip>
          <Tooltip title="Close editor" arrow>
            <IconButton size="small" onClick={onClose} aria-label="Close editor" sx={{ color: '#c5c5c5' }}>
              <CloseRoundedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </div>
      </header>

      <nav className="editor-tabs" role="tablist">
        {files.map((file, index) => (
          <button
            key={file.name}
            type="button"
            role="tab"
            aria-selected={index === activeIndex}
            className={`editor-tab ${index === activeIndex ? 'active' : ''}`}
            onClick={() => setActiveIndex(index)}
            title={file.name}
          >
            <DescriptionOutlinedIcon sx={{ fontSize: 14 }} />
            {file.name}
          </button>
        ))}
      </nav>

      <div className="editor-toolbar">
        <span className="editor-path">{active.name}</span>
        <span className="editor-toolbar-actions">
          <Tooltip title={`Download ${active.name}`} arrow>
            <button type="button" className="code-action" onClick={downloadFile} aria-label={`Download ${active.name}`}>
              <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />
            </button>
          </Tooltip>
          <CopyButton text={active.content} />
        </span>
      </div>

      <div className="editor-body">
        <HighlightedCode code={active.content.replace(/\n$/, '')} language={language} style={{ minHeight: '100%', fontSize: '14.5px' }} />
      </div>

      <footer className="editor-statusbar">
        <span>{languageLabel(language)}</span>
        <span>{lines} lines</span>
        <span>UTF-8</span>
      </footer>
    </aside>
  );
};

export default CodeEditorPane;
